import type { OrderRecord } from '@/types';

/**
 * Single demo dataset for the laundry dashboard.
 * All dashboard numbers and lists are derived from this array so cards never
 * contradict the lists. Replaced by the orders API later — nothing persisted.
 *
 * Amount model: `total = items subtotal (approved catalogue prices) + deliveryFee`,
 * so the order detail can show a coherent breakdown of item prices + delivery.
 *
 * Fulfillment methods (FB06):
 *  - self_dropoff_driver_delivery: customer drops, a driver delivers.
 *  - self_dropoff_self_pickup:     customer drops and picks up.
 *  - driver_pickup_self_pickup:    a driver picks up, customer picks up.
 *  - driver_pickup_driver_delivery: a driver picks up and delivers.
 */
export const mockOrders: OrderRecord[] = [
  // Awaiting laundry decision (status = new)
  {
    id: 'LB-1042',
    customerName: 'أحمد العتيبي',
    customerPhone: '0551234567',
    priority: 'urgent',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 95,
    deliveryFee: 15,
    status: 'new',
    createdAtDaysAgo: 0,
    createdAtMinutesAgo: 300,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: null,
    items: [
      { id: 'OI-1042-1', itemId: 'ITM-1001', itemName: 'ثوب', serviceType: 'wash_iron', quantity: 2 },
      { id: 'OI-1042-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_only', quantity: 3 },
    ],
  },
  {
    id: 'LB-1041',
    customerName: 'نورة الحربي',
    customerPhone: '0552345678',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 67,
    deliveryFee: 15,
    status: 'new',
    createdAtDaysAgo: 0,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: null,
    items: [
      { id: 'OI-1041-1', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_only', quantity: 2 },
      { id: 'OI-1041-2', itemId: 'ITM-1005', itemName: 'فستان', serviceType: 'wash_iron', quantity: 1 },
    ],
  },
  {
    id: 'LB-1040',
    customerName: 'سلطان القحطاني',
    customerPhone: '0553456789',
    priority: 'normal',
    fulfillment: 'self_dropoff_self_pickup',
    total: 38,
    deliveryFee: 0,
    status: 'new',
    createdAtDaysAgo: 0,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: null,
    items: [
      { id: 'OI-1040-1', itemId: 'ITM-1008', itemName: 'مفرش', serviceType: 'wash_only', quantity: 1 },
      { id: 'OI-1040-2', itemId: 'ITM-1003', itemName: 'بنطلون', serviceType: 'wash_only', quantity: 2 },
    ],
  },

  // In progress (status = in_progress)
  {
    id: 'LB-1039',
    customerName: 'ريم الدوسري',
    customerPhone: '0554567890',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 74,
    deliveryFee: 15,
    status: 'in_progress',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: 'سلطان الغامدي',
    delivery: null,
    items: [
      { id: 'OI-1039-1', itemId: 'ITM-1005', itemName: 'فستان', serviceType: 'wash_iron', quantity: 2 },
      { id: 'OI-1039-2', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_only', quantity: 1 },
    ],
  },
  {
    id: 'LB-1038',
    customerName: 'خالد الشمري',
    customerPhone: '0555678901',
    priority: 'urgent',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 95,
    deliveryFee: 15,
    status: 'in_progress',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: 'فهد الزهراني',
    delivery: null,
    items: [
      { id: 'OI-1038-1', itemId: 'ITM-1006', itemName: 'بدلة', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1038-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'iron_only', quantity: 4 },
    ],
  },

  // Accepted but not in progress (driver pickup method → searching for pickup)
  {
    id: 'LB-1037',
    customerName: 'مها الزهراني',
    customerPhone: '0556789012',
    priority: 'normal',
    fulfillment: 'driver_pickup_self_pickup',
    total: 60,
    deliveryFee: 12,
    status: 'accepted',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: null,
    items: [
      { id: 'OI-1037-1', itemId: 'ITM-1001', itemName: 'ثوب', serviceType: 'wash_only', quantity: 3 },
      { id: 'OI-1037-2', itemId: 'ITM-1003', itemName: 'بنطلون', serviceType: 'iron_only', quantity: 2 },
    ],
  },

  // Received & reviewing contents
  {
    id: 'LB-1036',
    customerName: 'فهد المطيري',
    customerPhone: '0557890123',
    priority: 'normal',
    fulfillment: 'self_dropoff_driver_delivery',
    total: 66,
    deliveryFee: 15,
    status: 'under_review',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: null,
    reviewAdopted: false,
    items: [
      { id: 'OI-1036-1', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_iron', quantity: 3 },
      { id: 'OI-1036-2', itemId: 'ITM-1003', itemName: 'بنطلون', serviceType: 'wash_only', quantity: 2 },
    ],
  },

  // Delivery stage - escalation TRIGGERED (wait, no driver, grace exceeded)
  {
    id: 'LB-1035',
    customerName: 'سارة العمري',
    customerPhone: '0558901234',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 68,
    deliveryFee: 15,
    status: 'ready_for_delivery',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: {
      inDeliveryStage: true,
      driverAvailable: false,
      customerChoice: 'wait',
      waitingMinutes: 45,
      driverAccepted: false,
    },
    items: [
      { id: 'OI-1035-1', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1035-2', itemId: 'ITM-1005', itemName: 'فستان', serviceType: 'wash_only', quantity: 2 },
    ],
  },

  // Delivery stage - waiting but INSIDE grace window (no alert) + smart box
  {
    id: 'LB-1034',
    customerName: 'يوسف الحربي',
    customerPhone: '0559012345',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 50,
    deliveryFee: 15,
    status: 'ready_for_delivery',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: {
      inDeliveryStage: true,
      driverAvailable: false,
      customerChoice: 'wait',
      waitingMinutes: 12,
      driverAccepted: false,
    },
    smartBox: {
      stage: 'delivery',
      description: 'الطلب مخصص للتسليم عبر البوكس الذكي رقم 2 وفق ما ورد من النظام.',
    },
    items: [
      { id: 'OI-1034-1', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_iron', quantity: 2 },
      { id: 'OI-1034-2', itemId: 'ITM-1003', itemName: 'بنطلون', serviceType: 'wash_iron', quantity: 1 },
    ],
  },

  // Delivery stage - driver already accepted (no alert)
  {
    id: 'LB-1027',
    customerName: 'وليد القرني',
    customerPhone: '0559123456',
    priority: 'urgent',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 138,
    deliveryFee: 18,
    status: 'ready_for_delivery',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: 'ماجد العنزي',
    delivery: {
      inDeliveryStage: true,
      driverAvailable: true,
      customerChoice: 'wait',
      waitingMinutes: 60,
      driverAccepted: true,
    },
    items: [
      { id: 'OI-1027-1', itemId: 'ITM-1006', itemName: 'بدلة', serviceType: 'wash_iron', quantity: 2 },
      { id: 'OI-1027-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'iron_only', quantity: 3 },
    ],
  },

  // Delivery stage - customer chose cancel (no alert)
  {
    id: 'LB-1026',
    customerName: 'عبدالعزيز السالم',
    customerPhone: '0559234567',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 59,
    deliveryFee: 15,
    status: 'ready_for_delivery',
    createdAtDaysAgo: 1,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: {
      inDeliveryStage: true,
      driverAvailable: false,
      customerChoice: 'cancel',
      waitingMinutes: 50,
      driverAccepted: false,
    },
    items: [
      { id: 'OI-1026-1', itemId: 'ITM-1001', itemName: 'ثوب', serviceType: 'wash_only', quantity: 2 },
      { id: 'OI-1026-2', itemId: 'ITM-1008', itemName: 'مفرش', serviceType: 'wash_only', quantity: 1 },
    ],
  },

  // Completed today
  {
    id: 'LB-1033',
    customerName: 'عبدالله الغامدي',
    customerPhone: '0559345678',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 70,
    deliveryFee: 15,
    status: 'completed',
    createdAtDaysAgo: 2,
    completedDaysAgo: 0,
    assignedDriver: 'ماجد العنزي',
    delivery: null,
    items: [
      { id: 'OI-1033-1', itemId: 'ITM-1006', itemName: 'بدلة', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1033-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'iron_only', quantity: 4 },
    ],
  },
  {
    id: 'LB-1032',
    customerName: 'هند العسيري',
    customerPhone: '0559456789',
    priority: 'normal',
    fulfillment: 'self_dropoff_self_pickup',
    total: 43,
    deliveryFee: 0,
    status: 'completed',
    createdAtDaysAgo: 2,
    completedDaysAgo: 0,
    assignedDriver: null,
    delivery: null,
    items: [
      { id: 'OI-1032-1', itemId: 'ITM-1005', itemName: 'فستان', serviceType: 'wash_only', quantity: 2 },
      { id: 'OI-1032-2', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_only', quantity: 1 },
    ],
  },
  {
    id: 'LB-1031',
    customerName: 'تركي السبيعي',
    customerPhone: '0559567890',
    priority: 'urgent',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 85,
    deliveryFee: 18,
    status: 'completed',
    createdAtDaysAgo: 2,
    completedDaysAgo: 0,
    assignedDriver: 'سلطان الغامدي',
    delivery: null,
    items: [
      { id: 'OI-1031-1', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1031-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_iron', quantity: 2 },
    ],
  },
  {
    id: 'LB-1030',
    customerName: 'لمى الجهني',
    customerPhone: '0559678901',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 55,
    deliveryFee: 15,
    status: 'completed',
    createdAtDaysAgo: 3,
    completedDaysAgo: 0,
    assignedDriver: 'فهد الزهراني',
    delivery: null,
    items: [
      { id: 'OI-1030-1', itemId: 'ITM-1001', itemName: 'ثوب', serviceType: 'wash_only', quantity: 2 },
      { id: 'OI-1030-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_only', quantity: 2 },
    ],
  },

  // Completed earlier (not today)
  {
    id: 'LB-1029',
    customerName: 'بدر العنزي',
    customerPhone: '0559789012',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 62,
    deliveryFee: 15,
    status: 'completed',
    createdAtDaysAgo: 2,
    completedDaysAgo: 1,
    assignedDriver: 'ماجد العنزي',
    delivery: null,
    items: [
      { id: 'OI-1029-1', itemId: 'ITM-1006', itemName: 'بدلة', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1029-2', itemId: 'ITM-1003', itemName: 'بنطلون', serviceType: 'iron_only', quantity: 2 },
    ],
  },

  // Cancelled by the customer
  {
    id: 'LB-1028',
    customerName: 'منى الشهري',
    customerPhone: '0559890123',
    priority: 'normal',
    fulfillment: 'self_dropoff_self_pickup',
    total: 16,
    deliveryFee: 0,
    status: 'cancelled',
    createdAtDaysAgo: 2,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: null,
    cancelReason: 'customer_cancelled',
    refundStatus: 'processing',
    items: [
      { id: 'OI-1028-1', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_only', quantity: 2 },
    ],
  },

  // Rejected by the laundry
  {
    id: 'LB-1025',
    customerName: 'عمر الثقفي',
    customerPhone: '0559901234',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 60,
    deliveryFee: 15,
    status: 'cancelled',
    createdAtDaysAgo: 3,
    completedDaysAgo: null,
    assignedDriver: null,
    delivery: null,
    cancelReason: 'laundry_rejected',
    refundStatus: 'pending',
    items: [
      { id: 'OI-1025-1', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1025-2', itemId: 'ITM-1008', itemName: 'مفرش', serviceType: 'wash_only', quantity: 1 },
    ],
  },

  // Older completed orders (support the finance periods: week / month)
  {
    id: 'LB-1024',
    customerName: 'ناصر الحمود',
    customerPhone: '0556012345',
    priority: 'normal',
    fulfillment: 'self_dropoff_self_pickup',
    total: 68,
    deliveryFee: 0,
    status: 'completed',
    createdAtDaysAgo: 4,
    completedDaysAgo: 3,
    assignedDriver: null,
    delivery: null,
    items: [
      { id: 'OI-1024-1', itemId: 'ITM-1006', itemName: 'بدلة', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1024-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_iron', quantity: 3 },
    ],
  },
  {
    id: 'LB-1023',
    customerName: 'غادة العنزي',
    customerPhone: '0556023456',
    priority: 'normal',
    fulfillment: 'driver_pickup_self_pickup',
    total: 51,
    deliveryFee: 12,
    status: 'completed',
    createdAtDaysAgo: 10,
    completedDaysAgo: 9,
    assignedDriver: 'سلطان الغامدي',
    delivery: null,
    items: [
      { id: 'OI-1023-1', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_only', quantity: 2 },
      { id: 'OI-1023-2', itemId: 'ITM-1005', itemName: 'فستان', serviceType: 'iron_only', quantity: 1 },
    ],
  },

  // Older completed orders that back the sub-driver review history, so every
  // review always points to an order that is genuinely completed.
  {
    id: 'LB-1022',
    customerName: 'ريم الدوسري',
    customerPhone: '0554567890',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 72,
    deliveryFee: 15,
    status: 'completed',
    createdAtDaysAgo: 1,
    completedDaysAgo: 0,
    assignedDriver: 'سلطان الغامدي',
    delivery: null,
    items: [
      { id: 'OI-1022-1', itemId: 'ITM-1005', itemName: 'فستان', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1022-2', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_only', quantity: 1 },
    ],
  },
  {
    id: 'LB-1021',
    customerName: 'نورة الحربي',
    customerPhone: '0552345678',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 63,
    deliveryFee: 15,
    status: 'completed',
    createdAtDaysAgo: 3,
    completedDaysAgo: 2,
    assignedDriver: 'سلطان الغامدي',
    delivery: null,
    items: [
      { id: 'OI-1021-1', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_only', quantity: 2 },
      { id: 'OI-1021-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'wash_iron', quantity: 1 },
    ],
  },
  {
    id: 'LB-1020',
    customerName: 'خالد الشمري',
    customerPhone: '0555678901',
    priority: 'urgent',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 92,
    deliveryFee: 18,
    status: 'completed',
    createdAtDaysAgo: 2,
    completedDaysAgo: 1,
    assignedDriver: 'فهد الزهراني',
    delivery: null,
    items: [
      { id: 'OI-1020-1', itemId: 'ITM-1006', itemName: 'بدلة', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1020-2', itemId: 'ITM-1002', itemName: 'قميص', serviceType: 'iron_only', quantity: 3 },
    ],
  },
  {
    id: 'LB-1019',
    customerName: 'وليد القرني',
    customerPhone: '0559123456',
    priority: 'normal',
    fulfillment: 'driver_pickup_self_pickup',
    total: 58,
    deliveryFee: 12,
    status: 'completed',
    createdAtDaysAgo: 6,
    completedDaysAgo: 5,
    assignedDriver: 'فهد الزهراني',
    delivery: null,
    items: [
      { id: 'OI-1019-1', itemId: 'ITM-1001', itemName: 'ثوب', serviceType: 'wash_only', quantity: 3 },
      { id: 'OI-1019-2', itemId: 'ITM-1003', itemName: 'بنطلون', serviceType: 'iron_only', quantity: 2 },
    ],
  },
  {
    id: 'LB-1018',
    customerName: 'عمر الثقفي',
    customerPhone: '0559901234',
    priority: 'normal',
    fulfillment: 'driver_pickup_driver_delivery',
    total: 61,
    deliveryFee: 15,
    status: 'completed',
    createdAtDaysAgo: 15,
    completedDaysAgo: 14,
    assignedDriver: 'عبدالله الدوسري',
    delivery: null,
    items: [
      { id: 'OI-1018-1', itemId: 'ITM-1004', itemName: 'عباية', serviceType: 'wash_iron', quantity: 1 },
      { id: 'OI-1018-2', itemId: 'ITM-1008', itemName: 'مفرش', serviceType: 'wash_only', quantity: 1 },
    ],
  },
];